/**
 * The vocabulary of "Candidatos" — the other half of the marketplace.
 *
 * There were 297 people in this database, every one of them at `lead_status =
 * 'new'`, and no screen anywhere that moved them. The view that would have —
 * `CandidateTriageView` — was deleted along with the matching feature, and
 * `talents.ts` still refers to it three times. Meanwhile 40 of 41 applications
 * sat unanswered, reachable only through the vacancy they were made against: to
 * find somebody waiting on a reply you had to already know which posting they
 * applied to.
 *
 * This is the same shape "Empresas que contratan" ended up with, for the same
 * reason. A screen that shows state — a status column, a strength score, a
 * questionnaire — answers "what do we know about this person" and never "what do
 * I do next", and a workspace whose landing tab is a status is a filing cabinet.
 *
 * ── What it is built on, and what it is deliberately not ─────────────
 *
 * Only columns that already exist: `talents.lead_status` and the status of the
 * applications a person actually made. No new tables, no follow-up dates, no
 * per-candidate activity log — the employer side has those because outreach is a
 * sequence of acts over weeks, and answering a candidate is not.
 *
 * And nothing here pairs a candidate with a vacancy they did not apply to. The
 * only link between a person and a job is one they made themselves, which is the
 * rule the deleted feature broke.
 */

// ── Statuses, as the two tables spell them ───────────────────────────

export type TalentStatus = "new" | "contacted" | "placed";

/**
 * What each filter in the queue holds, in one line, under the chips.
 *
 * The same rule Correo now follows: a filter whose name you have to guess at
 * is a filter nobody presses. "Por correo" in particular said nothing — it
 * meant "these people arrived by writing to us rather than through the form",
 * which is worth knowing and was not deducible from two words.
 */
export const TALENT_TAB_HINT: Record<string, string> = {
    answer: "Se postularon y todavía no les hemos contestado. Primero los que llevan más tiempo esperando.",
    todo: "Todo lo que tiene algo pendiente: contestar una candidatura o retomar un contacto que se quedó a medias.",
    email: "Personas que llegaron escribiéndonos un correo, no por el formulario de la web.",
    new: "Nadie les ha cambiado el estado todavía. Es el estado con el que entran todos.",
    contacted: "Alguien del equipo ya les escribió.",
    placed: "Los contrató una empresa."
};

export const TALENT_TABS: { id: TalentStatus; label: string }[] = [
    { id: "new", label: "Sin revisar" },
    { id: "contacted", label: "Contactados" },
    { id: "placed", label: "Contratados" }
];

export const TALENT_STATUS_COLOR: Record<TalentStatus, string> = {
    new: "grayLighter",
    contacted: "blueLighter",
    placed: "greenLighter"
};

export function talentStatusLabel(status: TalentStatus | string | null | undefined): string {
    return TALENT_TABS.find(t => t.id === status)?.label ?? String(status ?? "");
}

export type ApplicationStatus = "applied" | "reviewing" | "interview" | "rejected" | "hired";

export const APPLICATION_LABEL: Record<ApplicationStatus, string> = {
    applied: "Sin contestar",
    reviewing: "En revisión",
    interview: "Entrevista",
    rejected: "Descartado",
    hired: "Contratado"
};

export const APPLICATION_COLOR: Record<ApplicationStatus, string> = {
    applied: "blueLighter",
    reviewing: "orangeLighter",
    interview: "purpleLighter",
    rejected: "redLighter",
    hired: "greenLighter"
};

/** Offered as buttons, in the order a decision actually moves. */
export const APPLICATION_ACTIONS: ApplicationStatus[] = ["reviewing", "interview", "hired", "rejected"];

/** An application still waiting on a decision from us. */
export function isOpenApplication(status: string | null | undefined): boolean {
    return status === "applied" || status === "reviewing" || status === "interview";
}

// ── Rows, as the data plane returns them ─────────────────────────────

export interface TalentRow {
    id: string;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    linkedin_url?: string | null;
    cv_url?: string | null;
    location?: string | null;
    professional_objective?: string | null;
    lead_status?: TalentStatus | null;
    created_at?: string | null;
    auth_user_id?: string | null;
    area_keys?: string[] | null;
    strength_score?: number | null;
    experience_floor?: number | null;
    questionnaire_completed_at?: string | null;
    origin?: string | null;
    /**
     * The rest of the row, which nothing read until the cover did.
     *
     * These were absent from this interface, not from the table: the queue
     * fetched whole rows and then described a person with six of their fields,
     * which is the complaint the cover exists to answer. Every one is a real
     * column on `talents` — the counts and `profile_inflated` are generated
     * (drizzle/0025), the `_canon` arrays are the normalized halves of the
     * candidate's own free text, and `photo_url`/`profile_confirmed_at` arrived
     * with 0045 and 0046.
     */
    photo_url?: string | null;
    questionnaire?: Record<string, unknown> | null;
    certifications_canon?: string[] | null;
    skills_canon?: string[] | null;
    advanced_area_count?: number | null;
    strong_area_count?: number | null;
    profile_inflated?: boolean | null;
    profile_confirmed_at?: string | null;
    linkedin_linked_at?: string | null;
    /** Free labels the team put on them (drizzle/0054). */
    tags?: string[] | null;
    /**
     * What we sent them, and whether the address still works (drizzle/0057).
     *
     * Beside `profile_confirmed_at` rather than among the questionnaire fields,
     * because that is the date they are read against: a nudge older than the
     * confirmation is a nudge that was answered.
     */
    profile_nudge_sent_at?: string | null;
    profile_nudge_reminded_at?: string | null;
    email_bounced_at?: string | null;
    digest_invited_at?: string | null;
}

export interface ApplicationRow {
    id: string;
    talent_id?: string | null;
    auth_user_id?: string | null;
    job_id?: string | null;
    status?: ApplicationStatus | null;
    created_at?: string | null;
}

/** One candidate, with everything this screen knows about them. */
export interface Candidate {
    id: string;
    name: string;
    email: string | null;
    row: TalentRow;
    status: TalentStatus;
    /** Their own applications, newest first. The only person↔job link there is. */
    applications: ApplicationRow[];
    /** Applications still waiting on a decision from us. */
    openApplications: ApplicationRow[];
    /** The oldest unanswered application, which is what the queue sorts on. */
    waitingSince: string | null;
}

// ── What to do next ──────────────────────────────────────────────────

/**
 * The one thing a candidate needs next, derived rather than declared.
 *
 * Ordered by who is waiting and how visibly. Somebody who applied to a vacancy
 * knows exactly how long ago they did it and is refreshing an inbox; somebody
 * who filled in the questionnaire and heard nothing is a slower kind of loss.
 * A status column could not tell those apart — all 297 read `new`.
 */
/**
 * `review` was a stage — "Revisar el perfil" — and it is gone. It asked the
 * operator to look at every person who had ever registered, one by one, and
 * press "Marcar como revisado": 365 of them on 2026-09-06, zero ever moved,
 * and nothing downstream of the click. A profile is looked at when something
 * brings it — they applied, they wrote, a process is open that fits — and
 * none of those is "they registered". A person nobody owes anything to has
 * no stage, which is what `none` says.
 */
export type TalentStage = "answer" | "followup" | "done" | "none";

export function talentStageOf(candidate: Pick<Candidate, "status" | "openApplications">): TalentStage {
    // A live application outranks everything, including a candidate somebody
    // already marked "contacted": being contacted about one thing is not an
    // answer about another.
    if (candidate.openApplications.length > 0) return "answer";
    if (candidate.status === "placed") return "done";
    if (candidate.status === "contacted") return "followup";
    return "none";
}

export const TALENT_STAGE_LABEL: Record<TalentStage, string> = {
    answer: "Responder a su candidatura",
    followup: "Retomar el contacto",
    done: "Contratado",
    none: "Nada pendiente"
};

export const TALENT_STAGE_COLOR: Record<TalentStage, string> = {
    answer: "redLighter",
    followup: "orangeLighter",
    done: "greenLighter",
    none: "grayLighter"
};

export const TALENT_STAGE_ORDER: Record<TalentStage, number> = {
    answer: 0, followup: 1, none: 2, done: 3
};

/** The stages that are work. "Por hacer" is exactly this set. */
export const TALENT_TODO_STAGES: TalentStage[] = ["answer", "followup"];

export function isTalentTodo(stage: TalentStage): boolean {
    return TALENT_TODO_STAGES.includes(stage);
}

/**
 * Why this candidate is at this stage, with their own facts in it.
 *
 * `jobTitle` is passed in rather than looked up here so this stays pure — the
 * view holds the vacancies it already loaded.
 */
export function talentStageHint(
    candidate: Candidate,
    jobTitle: (jobId: string | null | undefined) => string
): string {
    switch (talentStageOf(candidate)) {
        case "answer": {
            const oldest = candidate.openApplications[candidate.openApplications.length - 1];
            const title = jobTitle(oldest?.job_id);
            const count = candidate.openApplications.length;
            return count === 1
                ? `Se postuló a "${title}" y sigue esperando respuesta.`
                : `Tiene ${count} candidaturas sin resolver, la más antigua a "${title}".`;
        }
        case "followup":
            return "Ya se le contactó y no hay nada más en marcha.";
        case "none":
            return candidate.row.origin === "email"
                ? "Escribió al buzón y su ficha se creó desde Correo. La conversación está allí."
                : candidate.row.questionnaire_completed_at
                    ? "Rellenó el cuestionario. No hay nada pendiente con esta persona: se mira su perfil cuando algo lo trae."
                    : "Se registró. No hay nada pendiente con esta persona.";
        case "done":
            return "";
    }
}

/**
 * Whether the toolbar's sort is one somebody picked, rather than the
 * collection's default that the controller starts from.
 *
 * "Any sort is set" used to be the test, and since rebase d29cb85da stopped
 * discarding `collection.sort` after mount it was true on every visit: the
 * queue took the default meant for Lista and Tabla as a choice, dropped its
 * stage grouping and showed a flat list. Picking the default by hand reads as
 * no choice at all — the same equality rebase's own "reset to default" uses.
 */
export function isChosenSort(sortBy: unknown, collectionSort: unknown): boolean {
    if (!Array.isArray(sortBy) || sortBy.length === 0) return false;
    return JSON.stringify(sortBy) !== JSON.stringify(sortKeys(collectionSort));
}

/** `collection.sort` in the list form the controller holds: one tuple or several. */
function sortKeys(sort: unknown): unknown[] | undefined {
    if (!Array.isArray(sort) || sort.length === 0) return undefined;
    return Array.isArray(sort[0]) ? sort : [sort];
}

/**
 * The queue's own order — and the one rule about when it must not apply.
 *
 * Stage first (who is waiting most visibly), then who has waited longest, then
 * the strongest profiles, then the newest registrations. That derived order is
 * the reason this view exists: none of it is a column, so no `ORDER BY` can
 * express it.
 *
 * `explicitSort` is what keeps it honest. The toolbar above the view has a sort
 * control; the rows come back in whatever order it asked for, and re-sorting
 * them here made that control inert — drawn, clickable, and with no effect on
 * screen. When somebody has set a sort, the incoming order *is* the answer and
 * this returns it untouched.
 */
export function orderQueue(
    candidates: Candidate[],
    options: { tab: string; explicitSort: boolean; stageOf?: (c: Candidate) => TalentStage }
): Candidate[] {
    if (options.explicitSort) return candidates;
    const stageOf = options.stageOf ?? talentStageOf;

    return [...candidates].sort((a, b) => {
        if (options.tab === "todo") {
            const byStage = TALENT_STAGE_ORDER[stageOf(a)] - TALENT_STAGE_ORDER[stageOf(b)];
            if (byStage !== 0) return byStage;
        }
        // Whoever has been waiting longest, first — inside the answer batch this
        // is the only order that is not arbitrary, and it is the one thing the
        // person on the other end is measuring.
        if (a.waitingSince || b.waitingSince) {
            return (a.waitingSince ?? "9999").localeCompare(b.waitingSince ?? "9999");
        }
        // Then the strongest profiles, then the newest registrations.
        const byStrength = (b.row.strength_score ?? 0) - (a.row.strength_score ?? 0);
        if (byStrength !== 0) return byStrength;
        return (b.row.created_at ?? "").localeCompare(a.row.created_at ?? "");
    });
}

// ── Reading a person ─────────────────────────────────────────────────

export function talentLabel(row: TalentRow): string {
    return (row.full_name ?? "").trim() || (row.email ?? "").trim() || "(sin nombre)";
}

/**
 * Build the screen's model from the three tables it reads.
 *
 * Applications join to a person by `talent_id` when they have one and by
 * `auth_user_id` when they do not — 0030 backfilled the first and the second is
 * what the older rows carry. Reading only one of them loses applications
 * silently, which on this screen means a person waiting on a reply who never
 * appears in the queue.
 */
export function buildCandidates(talents: TalentRow[], applications: ApplicationRow[]): Candidate[] {
    const byId = new Map<string, ApplicationRow[]>();
    const byAuthUser = new Map<string, ApplicationRow[]>();
    for (const application of applications) {
        if (application.talent_id) {
            const list = byId.get(application.talent_id);
            if (list) list.push(application); else byId.set(application.talent_id, [application]);
        } else if (application.auth_user_id) {
            const list = byAuthUser.get(application.auth_user_id);
            if (list) list.push(application); else byAuthUser.set(application.auth_user_id, [application]);
        }
    }

    return talents.map(row => {
        const seen = new Map<string, ApplicationRow>();
        for (const application of byId.get(row.id) ?? []) seen.set(application.id, application);
        if (row.auth_user_id) {
            for (const application of byAuthUser.get(row.auth_user_id) ?? []) seen.set(application.id, application);
        }
        const applicationsForRow = [...seen.values()]
            .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
        const open = applicationsForRow.filter(a => isOpenApplication(a.status));

        return {
            id: row.id,
            name: talentLabel(row),
            email: (row.email ?? "").trim() || null,
            row,
            status: (row.lead_status ?? "new") as TalentStatus,
            applications: applicationsForRow,
            openApplications: open,
            // The oldest of them: a person with three unanswered applications
            // has been waiting since the first one, not the last.
            waitingSince: open.length > 0
                ? open[open.length - 1]?.created_at ?? null
                : null
        };
    });
}

// ── Closing a process, for everyone who applied to it ────────────────

/**
 * The mail that closes a process, to every applicant at once.
 *
 * Thirty-eight people who applied through the site to the Basura Cero
 * posting waited a month with no answer, while the people who applied by
 * email got this exact message on 2026-08-18 — one at a time, by hand. The
 * wording is that message's, lifted from the mailbox rather than rewritten:
 * it is what the team already says. `title` is the vacancy as the posting
 * spelled it; the greeting takes the first name and falls back to nothing
 * rather than to "Hola candidato".
 *
 * Pure, so the subject and the body can be pinned by a test and the queue
 * can show them before sending — the operator edits the text if they want,
 * and what they send is what they saw.
 */
export function closeProcessEmail(input: { title: string; firstName?: string | null }): { subject: string; body: string } {
    const name = (input.firstName ?? "").trim();
    const greeting = name ? `Hola ${name},` : "Hola,";
    return {
        subject: `Sobre tu candidatura a «${input.title}»`,
        body: `${greeting}

Queremos agradecerte sinceramente por habernos enviado tu candidatura a «${input.title}». Te escribimos para informarte que el proceso de selección ha finalizado y ya hemos cubierto la vacante. Sin embargo, hemos revisado tu experiencia y nos gustaría mantener el contacto contigo.

Por ello, te invitamos a que completes tu perfil en sustentalent.com para poder considerarte en futuras vacantes que se ajusten a tus habilidades. Es el canal principal que nuestro equipo utiliza para buscar y contactar directamente a los candidatos cuando surgen nuevas oportunidades.

Agradecemos nuevamente el tiempo que dedicaste a esta candidatura y te deseamos mucho éxito en tus proyectos y en tu desarrollo profesional.

Esperamos verte pronto por SustenTalent.

Atentamente,
Equipo SustenTalent`
    };
}

/** "Ana García Pérez" → "Ana". The part of a name a greeting uses. */
export function firstNameOf(fullName: string | null | undefined): string {
    return (fullName ?? "").trim().split(/\s+/)[0] ?? "";
}
